import { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Tag, Table, Progress, Spin, message } from 'antd';
import {
  ShoppingCartOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  RiseOutlined,
  BugOutlined
} from '@ant-design/icons';
import { http } from '../../utils';
import moment from 'moment';
import './index.scss';

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState(null);
  const [errorRate, setErrorRate] = useState(null);
  const [errorHourly, setErrorHourly] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, trendRes, errorRateRes, errorHourlyRes] = await Promise.all([
        http.get('/api/dashboard/statistics'),
        http.get('/api/dashboard/trend', { params: { days: 30 } }),
        http.get('/api/error-log/rate', { params: { minutes: 60 } }),
        http.get('/api/error-log/hourly-rate', { params: { hours: 24 } })
      ]);
      if (statsRes.code === '0') setStats(statsRes.data);
      if (trendRes.code === '0') setTrend(trendRes.data);
      if (errorRateRes.success) setErrorRate(errorRateRes.data);
      if (errorHourlyRes.success) setErrorHourly(errorHourlyRes.data);
    } catch (e) {
      message.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!stats) return null;

  const { orders, quotes, handoff, quoteSuccessRate, countryStats, recentOrders } = stats;
  const totalProcessed = handoff.pending + handoff.partial + handoff.complete;

  const recentColumns = [
    {
      title: 'Order',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <span style={{ fontWeight: 500 }}>{name}</span>
    },
    {
      title: 'Country',
      dataIndex: 'countryCode',
      key: 'countryCode',
      render: (code) => <Tag>{code}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      render: (state) => {
        if (state === '2') return <Tag color="success">Completed</Tag>;
        if (state === '1') return <Tag color="warning">Partial</Tag>;
        return <Tag color="default">Pending</Tag>;
      }
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => moment(date).format('MM-DD HH:mm')
    }
  ];

  const maxTrendCount = trend
    ? Math.max(...(trend.orderTrend || []).map(d => d.count), 1)
    : 1;

  return (
    <div className="dashboard-container">
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable className="stat-card stat-card--orders">
            <Statistic
              title="Today's Orders"
              value={orders.today}
              prefix={<ShoppingCartOutlined />}
              suffix={<span className="stat-sub">/ {orders.week} this week</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable className="stat-card stat-card--quotes">
            <Statistic
              title="Today's Quotes"
              value={quotes.today}
              prefix={<SafetyCertificateOutlined />}
              suffix={<span className="stat-sub">/ {quotes.week} this week</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable className="stat-card stat-card--success">
            <Statistic
              title="Handoff Success Rate"
              value={quoteSuccessRate}
              prefix={<CheckCircleOutlined />}
              suffix="%"
              valueStyle={{ color: quoteSuccessRate >= 80 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable className="stat-card stat-card--total">
            <Statistic
              title="Monthly Orders"
              value={orders.month}
              prefix={<RiseOutlined />}
              suffix={<span className="stat-sub">/ {orders.total} total</span>}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card title="Order Trend (Last 30 Days)" className="trend-card">
            {(!trend?.orderTrend || trend.orderTrend.length === 0) ? (
              <div className="trend-empty">No data available</div>
            ) : (
              <div className="line-chart-container">
                {(() => {
                  const chartWidth = 700;
                  const chartHeight = 180;
                  const padX = 30;
                  const padTop = 20;
                  const padBottom = 30;
                  const drawWidth = chartWidth - padX * 2;
                  const drawHeight = chartHeight - padTop - padBottom;
                  const data = trend.orderTrend;
                  const stepX = data.length > 1 ? drawWidth / (data.length - 1) : drawWidth;

                  const getX = (idx) => padX + idx * stepX;
                  const getY = (count) => padTop + drawHeight - (count / maxTrendCount) * drawHeight;

                  const linePoints = data.map((item, idx) => `${getX(idx)},${getY(item.count)}`).join(' ');
                  const areaPath = `M${getX(0)},${padTop + drawHeight} L${data.map((item, idx) => `${getX(idx)},${getY(item.count)}`).join(' L')} L${getX(data.length - 1)},${padTop + drawHeight} Z`;

                  return (
                    <svg className="line-chart" viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
                      <defs>
                        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1677ff" stopOpacity="0.15" />
                          <stop offset="100%" stopColor="#1677ff" stopOpacity="0.01" />
                        </linearGradient>
                      </defs>
                      {/* Grid lines */}
                      {[0.25, 0.5, 0.75, 1].map((ratio) => (
                        <line
                          key={ratio}
                          x1={padX}
                          y1={padTop + drawHeight - ratio * drawHeight}
                          x2={chartWidth - padX}
                          y2={padTop + drawHeight - ratio * drawHeight}
                          stroke="#f0f0f0"
                          strokeWidth="0.5"
                        />
                      ))}
                      {/* Baseline */}
                      <line x1={padX} y1={padTop + drawHeight} x2={chartWidth - padX} y2={padTop + drawHeight} stroke="#e8e8e8" strokeWidth="0.5" />
                      {/* Area fill */}
                      <path d={areaPath} fill="url(#areaGradient)" />
                      {/* Line */}
                      <polyline
                        points={linePoints}
                        fill="none"
                        stroke="#1677ff"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />
                      {/* Data points & tooltips */}
                      {data.map((item, idx) => {
                        const x = getX(idx);
                        const y = getY(item.count);
                        const label = String(item.count);
                        const tooltipWidth = Math.max(label.length * 8 + 12, 36);
                        const tooltipX = x - tooltipWidth / 2;
                        const tooltipY = Math.max(y - 30, 2);
                        return (
                          <g key={item.date} className="line-chart-point-group">
                            <circle cx={x} cy={y} r="10" fill="transparent" className="line-chart-hover-area" />
                            <circle cx={x} cy={y} r="2.5" fill="#fff" stroke="#1677ff" strokeWidth="1.5" className="line-chart-point" />
                            <g className="line-chart-tooltip">
                              <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height="18" rx="4" fill="rgba(0,0,0,0.8)" />
                              <text x={x} y={tooltipY + 13} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="500">{item.count}</text>
                            </g>
                          </g>
                        );
                      })}
                      {/* X-axis labels */}
                      {data.map((item, idx) => {
                        const showLabel = idx % Math.ceil(data.length / 8) === 0 || idx === data.length - 1;
                        if (!showLabel) return null;
                        return (
                          <text
                            key={`label-${item.date}`}
                            x={getX(idx)}
                            y={chartHeight - 5}
                            textAnchor="middle"
                            fill="#999"
                            fontSize="9"
                          >
                            {moment(item.date).format('MM/DD')}
                          </text>
                        );
                      })}
                    </svg>
                  );
                })()}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Handoff Status" className="status-card">
            <div className="handoff-stats">
              <div className="handoff-item">
                <span><CheckCircleOutlined style={{ color: '#52c41a' }} /> Completed</span>
                <span className="handoff-count">{handoff.complete}</span>
              </div>
              <Progress
                percent={totalProcessed > 0 ? Math.round((handoff.complete / totalProcessed) * 100) : 0}
                strokeColor="#52c41a"
                size="small"
              />
              <div className="handoff-item" style={{ marginTop: 12 }}>
                <span><ClockCircleOutlined style={{ color: '#faad14' }} /> Partial</span>
                <span className="handoff-count">{handoff.partial}</span>
              </div>
              <Progress
                percent={totalProcessed > 0 ? Math.round((handoff.partial / totalProcessed) * 100) : 0}
                strokeColor="#faad14"
                size="small"
              />
              <div className="handoff-item" style={{ marginTop: 12 }}>
                <span><ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> Pending</span>
                <span className="handoff-count">{handoff.pending}</span>
              </div>
              <Progress
                percent={totalProcessed > 0 ? Math.round((handoff.pending / totalProcessed) * 100) : 0}
                strokeColor="#ff4d4f"
                size="small"
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card title="Recent Orders" className="recent-card">
            <Table
              columns={recentColumns}
              dataSource={recentOrders}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Orders by Country" className="country-card">
            <div className="country-stats">
              {countryStats.map((item) => (
                <div className="country-item" key={item.country}>
                  <div className="country-info">
                    <Tag color="blue">{item.country}</Tag>
                    <span>{item.count} orders</span>
                  </div>
                  <Progress
                    percent={Math.round((item.count / (orders.total || 1)) * 100)}
                    size="small"
                    strokeColor="#1677ff"
                  />
                </div>
              ))}
              {countryStats.length === 0 && <div className="trend-empty">No data</div>}
            </div>
          </Card>
        </Col>
      </Row>

      {errorRate && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card
              title={
                <span>
                  <BugOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
                  Error Rate (Last {errorRate.minutes} min)
                  <span className="error-rate-summary">
                    {errorRate.totalRecent} errors &middot; ~{errorRate.rate}/min
                  </span>
                </span>
              }
              className="error-rate-card"
            >
              {errorRate.trend && errorRate.trend.length > 0 ? (
                <div className="error-rate-chart-container">
                  {(() => {
                    const chartWidth = 900;
                    const chartHeight = 140;
                    const padX = 40;
                    const padTop = 15;
                    const padBottom = 25;
                    const drawWidth = chartWidth - padX * 2;
                    const drawHeight = chartHeight - padTop - padBottom;
                    const data = errorRate.trend;
                    const maxCount = Math.max(...data.map(d => d.count), 1);
                    const stepX = data.length > 1 ? drawWidth / (data.length - 1) : drawWidth;

                    const getX = (idx) => padX + idx * stepX;
                    const getY = (count) => padTop + drawHeight - (count / maxCount) * drawHeight;

                    const linePoints = data.map((item, idx) => `${getX(idx)},${getY(item.count)}`).join(' ');
                    const areaPath = `M${getX(0)},${padTop + drawHeight} L${data.map((item, idx) => `${getX(idx)},${getY(item.count)}`).join(' L')} L${getX(data.length - 1)},${padTop + drawHeight} Z`;

                    return (
                      <svg className="error-rate-chart" viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
                        <defs>
                          <linearGradient id="errorAreaGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#ff4d4f" stopOpacity="0.2" />
                            <stop offset="100%" stopColor="#ff4d4f" stopOpacity="0.02" />
                          </linearGradient>
                        </defs>
                        {[0.25, 0.5, 0.75, 1].map((ratio) => (
                          <line
                            key={ratio}
                            x1={padX}
                            y1={padTop + drawHeight - ratio * drawHeight}
                            x2={chartWidth - padX}
                            y2={padTop + drawHeight - ratio * drawHeight}
                            stroke="#f5f5f5"
                            strokeWidth="0.5"
                          />
                        ))}
                        <line x1={padX} y1={padTop + drawHeight} x2={chartWidth - padX} y2={padTop + drawHeight} stroke="#e8e8e8" strokeWidth="0.5" />
                        {/* Y-axis labels */}
                        {[0, 0.5, 1].map((ratio) => (
                          <text
                            key={`y-${ratio}`}
                            x={padX - 5}
                            y={padTop + drawHeight - ratio * drawHeight + 3}
                            textAnchor="end"
                            fill="#999"
                            fontSize="9"
                          >
                            {Math.round(maxCount * ratio)}
                          </text>
                        ))}
                        <path d={areaPath} fill="url(#errorAreaGradient)" />
                        <polyline
                          points={linePoints}
                          fill="none"
                          stroke="#ff4d4f"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                        />
                        {data.map((item, idx) => {
                          const x = getX(idx);
                          const y = getY(item.count);
                          const label = String(item.count);
                          const tooltipWidth = Math.max(label.length * 8 + 12, 36);
                          const tooltipX = x - tooltipWidth / 2;
                          const tooltipY = Math.max(y - 28, 2);
                          return (
                            <g key={item.minute} className="error-rate-point-group">
                              <circle cx={x} cy={y} r="8" fill="transparent" className="error-rate-hover-area" />
                              <circle cx={x} cy={y} r="2" fill="#fff" stroke="#ff4d4f" strokeWidth="1.5" className="error-rate-point" />
                              <g className="error-rate-tooltip">
                                <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height="16" rx="3" fill="rgba(0,0,0,0.75)" />
                                <text x={x} y={tooltipY + 12} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="500">{item.count}</text>
                              </g>
                            </g>
                          );
                        })}
                        {/* X-axis labels */}
                        {data.map((item, idx) => {
                          const showLabel = idx % Math.ceil(data.length / 10) === 0 || idx === data.length - 1;
                          if (!showLabel) return null;
                          const timeLabel = item.minute.split(' ')[1] || item.minute;
                          return (
                            <text
                              key={`elabel-${item.minute}`}
                              x={getX(idx)}
                              y={chartHeight - 5}
                              textAnchor="middle"
                              fill="#999"
                              fontSize="8"
                            >
                              {timeLabel}
                            </text>
                          );
                        })}
                      </svg>
                    );
                  })()}
                </div>
              ) : (
                <div className="trend-empty">No errors in the last {errorRate.minutes} minutes</div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {errorHourly && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card
              title={
                <span>
                  <BugOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                  Hourly Error Rate (Last 24h)
                  <span className="error-rate-summary">
                    {errorHourly.totalRecent} errors &middot; ~{errorHourly.rate}/hour
                  </span>
                </span>
              }
              className="error-rate-card"
            >
              {errorHourly.trend && errorHourly.trend.length > 0 ? (
                <div className="error-rate-chart-container">
                  {(() => {
                    const chartWidth = 900;
                    const chartHeight = 160;
                    const padX = 40;
                    const padTop = 15;
                    const padBottom = 30;
                    const drawWidth = chartWidth - padX * 2;
                    const drawHeight = chartHeight - padTop - padBottom;
                    const data = errorHourly.trend;
                    const maxCount = Math.max(...data.map(d => d.count), 1);
                    const stepX = data.length > 1 ? drawWidth / (data.length - 1) : drawWidth;

                    const getX = (idx) => padX + idx * stepX;
                    const getY = (count) => padTop + drawHeight - (count / maxCount) * drawHeight;

                    const linePoints = data.map((item, idx) => `${getX(idx)},${getY(item.count)}`).join(' ');
                    const areaPath = `M${getX(0)},${padTop + drawHeight} L${data.map((item, idx) => `${getX(idx)},${getY(item.count)}`).join(' L')} L${getX(data.length - 1)},${padTop + drawHeight} Z`;

                    return (
                      <svg className="error-rate-chart error-rate-chart--hourly" viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
                        <defs>
                          <linearGradient id="errorHourlyGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#fa8c16" stopOpacity="0.2" />
                            <stop offset="100%" stopColor="#fa8c16" stopOpacity="0.02" />
                          </linearGradient>
                        </defs>
                        {[0.25, 0.5, 0.75, 1].map((ratio) => (
                          <line
                            key={ratio}
                            x1={padX}
                            y1={padTop + drawHeight - ratio * drawHeight}
                            x2={chartWidth - padX}
                            y2={padTop + drawHeight - ratio * drawHeight}
                            stroke="#f5f5f5"
                            strokeWidth="0.5"
                          />
                        ))}
                        <line x1={padX} y1={padTop + drawHeight} x2={chartWidth - padX} y2={padTop + drawHeight} stroke="#e8e8e8" strokeWidth="0.5" />
                        {[0, 0.5, 1].map((ratio) => (
                          <text
                            key={`yh-${ratio}`}
                            x={padX - 5}
                            y={padTop + drawHeight - ratio * drawHeight + 3}
                            textAnchor="end"
                            fill="#999"
                            fontSize="9"
                          >
                            {Math.round(maxCount * ratio)}
                          </text>
                        ))}
                        <path d={areaPath} fill="url(#errorHourlyGradient)" />
                        <polyline
                          points={linePoints}
                          fill="none"
                          stroke="#fa8c16"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                        />
                        {data.map((item, idx) => {
                          const x = getX(idx);
                          const y = getY(item.count);
                          const label = String(item.count);
                          const tooltipWidth = Math.max(label.length * 8 + 12, 36);
                          const tooltipX = x - tooltipWidth / 2;
                          const tooltipY = Math.max(y - 28, 2);
                          return (
                            <g key={item.hour} className="error-rate-point-group">
                              <circle cx={x} cy={y} r="10" fill="transparent" className="error-rate-hover-area" />
                              <circle cx={x} cy={y} r="2.5" fill="#fff" stroke="#fa8c16" strokeWidth="1.5" className="error-rate-point" />
                              <g className="error-rate-tooltip">
                                <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height="16" rx="3" fill="rgba(0,0,0,0.75)" />
                                <text x={x} y={tooltipY + 12} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="500">{item.count}</text>
                              </g>
                            </g>
                          );
                        })}
                        {data.map((item, idx) => {
                          const timeLabel = item.hour.split(' ')[1] || item.hour;
                          return (
                            <text
                              key={`hlabel-${item.hour}`}
                              x={getX(idx)}
                              y={chartHeight - 8}
                              textAnchor="middle"
                              fill="#999"
                              fontSize="9"
                            >
                              {timeLabel}
                            </text>
                          );
                        })}
                      </svg>
                    );
                  })()}
                </div>
              ) : (
                <div className="trend-empty">No errors in the last 24 hours</div>
              )}
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Home;
